import React, { useEffect, useRef, useState } from "react";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import api from "API/api";
import { Box, Button, Stack, Typography } from "@mui/material";
import AddUsersModal from "components/Modals/AddUsersModal/AddUsersModal";
import colors from "assets/theme/base/colors";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DeleteUserModal from "components/Modals/DeleteUserModal/DeleteUserModal";
import { toast } from "sonner";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";

const Users = () => {
  const [data, setData] = useState([]);
  const { white, gradients } = colors;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  // const [totalResults, setTotalResults] = useState(0);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [siteData, setSiteData] = useState([]);
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const getAllUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/userapi`);
      setData(response?.data?.data);
      if (response?.data?.data?.length === 0) {
        toast.info("Data Not Available");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllUsers();
  }, []);

  const handleDeleteUser = async () => {
    try {
      let payload = {
        uid: currentUser.id,
      };
      const response = await api.delete(`/userapi`, { data: payload });
      toast.success(response.data.message);
      getAllUsers();
    } catch (error) {
      toast.error(error.response.data.message);
    }
    setDeleteModalOpen(false);
  };

  const ClickSiteList = ({ siteList }) => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const buttonRef = useRef(null);
    const popupRef = useRef(null);

    const handleClick = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
      setIsOpen((prev) => !prev);
    };

    const handleClickOutside = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        popupRef.current &&
        !popupRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    useEffect(() => {
      if (isOpen) document.addEventListener("pointerdown", handleClickOutside);
      else document.removeEventListener("pointerdown", handleClickOutside);

      return () => {
        document.removeEventListener("pointerdown", handleClickOutside);
      };
    }, [isOpen]);

    const popup = (
      <Box
        ref={popupRef}
        sx={{
          position: "absolute",
          top: coords.top,
          left: coords.left,
          zIndex: 1500,
          p: 2,
          borderRadius: "10px",
          backgroundColor: "#fff",
          boxShadow: "0px 6px 24px rgba(0,0,0,0.15)",
          border: "1px solid #ddd",
          width: 220,
          overflowY: "scroll",
          overflow: "hidden",
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {!siteList.length ? "No Location" : "Site Locations"}
        </Typography>
        {siteList.length > 0 ? (
          <Box
            component="ul"
            sx={{
              listStyle: "none",
              overflowY: "auto",
              maxHeight: 170,
              pr: 1,
              pt: 1,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f5f5f5",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ccc",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-button": {
                display: "none",
                height: 0,
              },
              scrollbarWidth: "thin",
              scrollbarColor: "#ccc #f5f5f5",
            }}
          >
            {siteList?.map((site, i) => (
              <li key={i}>
                <Typography
                  variant="body2"
                  onClick={() => navigate(`/scenarios/${site?.site_id}`)}
                  sx={{
                    cursor: "pointer",
                    fontWeight: 400,
                    "&:hover": { fontWeight: 600 },
                  }}
                >
                  • {site?.site_location}
                </Typography>
              </li>
            ))}
          </Box>
        ) : null}
      </Box>
    );

    return (
      <>
        <Box
          ref={buttonRef}
          onClick={handleClick}
          sx={{
            cursor: "pointer",
            color: "#1a73e8",
            fontWeight: 500,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {!siteList.length
            ? "No Sites"
            : `${siteList.length} Site${siteList.length > 1 ? "s" : ""}`}
        </Box>
        {isOpen && ReactDOM.createPortal(popup, document.body)}
      </>
    );
  };

  const columns = [
    { field: "username", headerName: "User", flex: 1, minWidth: 180 },
    { field: "email", headerName: "Email", flex: 2, minWidth: 250 },
    { field: "role", headerName: "Role", flex: 1, minWidth: 150 },
    { field: "phone", headerName: "Phone", flex: 1, minWidth: 180 },
    { field: "creator", headerName: "Creator", minWidth: 150, flex: 1 },
    {
      field: "created_at",
      headerName: "Created At",
      minWidth: 150,
      renderCell: (params) => {
        return (
          <Stack
            direction="column"
            gap={0.4}
            justifyContent="center"
            height={1}
            sx={{ color: "text.secondary" }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              {params.row.created_at?.split(" ")[0]}
            </Typography>
            <Typography variant="caption">
              {params.row.created_at?.split(" ")[1]}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "last_login",
      headerName: "Last Login",
      minWidth: 150,
      renderCell: (params) => {
        return (
          <Stack
            direction="column"
            gap={0.4}
            justifyContent="center"
            height={1}
            sx={{ color: "text.secondary" }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              {params.row.last_login?.split(" ")[0]}
            </Typography>
            <Typography variant="caption">
              {params.row.last_login?.split(" ")[1]}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "site",
      headerName: "Sites",
      flex: 2,
      minWidth: 120,
      renderCell: (params) => <ClickSiteList siteList={params.value} />,
    },
    {
      type: "actions",
      field: "actions",
      headerName: "Actions",
      align: "right",
      headerAlign: "right",
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      getActions: (params) => [
        <GridActionsCellItem
          showInMenu
          icon={<EditOutlinedIcon />}
          label="Edit"
          onClick={() => {
            const fullUser = data.find((u) => u.uid === params.row.id);
            setCurrentUser(fullUser);
            setAddUserOpen(true);
          }}
        />,
        <GridActionsCellItem
          showInMenu
          icon={<DeleteOutlineOutlinedIcon sx={{ color: "error.main" }} />}
          label="Delete"
          onClick={() => {
            setCurrentUser(params.row);
            setDeleteModalOpen(true);
          }}
          sx={{ color: "error.main" }}
        />,
      ],
    },
  ];

  const _dataGrid = data?.map((item, index) => {
    const siteList = item?.site_data?.length
      ? item.site_data.map((site) => site)
      : [];
    return {
      id: item?.uid || "-",
      username: item?.username || "-",
      creator: item?.creator || "-",
      email: item?.email || "-",
      phone: item?.phone || "-",
      created_at: item?.created_at || "-",
      last_login: item?.last_login || "-",
      role: item?.role || "-",
      firstname: item?.firstname || "",
      lastname: item?.lastname || "",
      site: siteList,
    };
  });

  const getAllSites = async () => {
    try {
      const response = await api.get("/cv/siteapi");
      setSiteData(response?.data?.data);
    } catch (error) {
      console.log(error, "errorr");
    } finally {
    }
  };

  useEffect(() => {
    getAllSites();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {user?.role === "admin" || user?.role === "user" ? (
        <Box sx={{ display: "flex", justifyContent: "end" }}>
          <Button
            onClick={() => {
              setAddUserOpen(true);
              setCurrentUser(null);
            }}
            sx={{
              height: "38px",
              width: "180px",
              fontSize: "14px",
              fontWeight: "500",
              borderRadius: "6px",
              background: `${gradients.info.state} !important`,
              color: `${white.main} !important`,
              border: "none",
              marginRight: "0",
              marginBottom: "0",
              "&:hover": {
                background: `${gradients.info.state} !important`,
                color: white.main,
              },
            }}
            className="add-user-btn"
          >
            Add User
          </Button>
        </Box>
      ) : (
        ""
      )}
      <DataGrid
        rowHeight={60}
        rows={_dataGrid}
        columns={columns}
        loading={loading}
        paginationMode="server"
        disableRowSelectionOnClick
        rowCount={_dataGrid?.length}
        pageSizeOptions={[25, 50, 100]}
        paginationModel={{ page: page, pageSize: pageSize }}
        initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
        onPaginationModelChange={(model) => {
          setPage(model.page);
          setPageSize(model.pageSize);
        }}
        sx={{
          mt: 2,
          fontSize: "15px",
          borderRadius: "8px",
          "& .MuiCircularProgress-root": { color: `${gradients.info.main}` },
          "& .MuiDataGrid-columnHeaderTitle": {
            color: "#fff",
            fontWeight: "500",
          },
          "& .MuiDataGrid-cell": {
            outlineOffset: "none !important",
            outline: "none !important",
          },
          "& .MuiDataGrid-columnHeader": {
            background: "#063863",
            "& .MuiSvgIcon-root": { color: "#fff !important" },
          },
          "& .MuiDataGrid-row": {
            "&:nth-of-type(odd)": { backgroundColor: "#f8f9fa" },
            "&:hover": { backgroundColor: "rgb(25 108 215 / 0.02) !important" },
          },
        }}
      />
      {addUserOpen && (
        <AddUsersModal
          addUserOpen={addUserOpen}
          setAddUserOpen={setAddUserOpen}
          currentUser={currentUser}
          getAllUsers={getAllUsers}
        />
      )}
      {deleteModalOpen && (
        <DeleteUserModal
          deleteModalOpen={deleteModalOpen}
          setDeleteModalOpen={setDeleteModalOpen}
          handleDeleteUser={handleDeleteUser}
          getAllUsers={getAllUsers}
        />
      )}
    </DashboardLayout>
  );
};
export default Users;
